/**
 * A bounded LRU of object URLs that revokes on eviction.
 *
 * Local files barely need this — an object URL over a File is a cheap handle. It exists for the
 * Google Photos source, which holds real decoded Blobs in memory and will happily OOM mobile
 * Safari if nothing evicts. Building it now keeps that source a drop-in later. See PLAN.md §4 S4.
 */

interface Entry {
  url: string;
  /** Called on eviction. Object URLs need revoking; pass-through URLs don't. */
  dispose: () => void;
}

export class ObjectUrlCache {
  private entries = new Map<string, Entry>();

  constructor(private readonly maxEntries: number) {}

  get(key: string): string | undefined {
    const entry = this.entries.get(key);
    if (!entry) return undefined;
    // Re-insert to mark as most-recently-used. Map preserves insertion order.
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.url;
  }

  set(key: string, url: string, dispose: () => void): void {
    const existing = this.entries.get(key);
    if (existing) {
      this.entries.delete(key);
      // Same URL for the same key is a no-op; a different one means the old is now garbage.
      if (existing.url !== url) existing.dispose();
    }
    this.entries.set(key, { url, dispose });

    while (this.entries.size > this.maxEntries) {
      const oldest = this.entries.keys().next();
      if (oldest.done) break;
      this.delete(oldest.value);
    }
  }

  delete(key: string): void {
    const entry = this.entries.get(key);
    if (!entry) return;
    this.entries.delete(key);
    entry.dispose();
  }

  clear(): void {
    for (const entry of this.entries.values()) entry.dispose();
    this.entries.clear();
  }

  get size(): number {
    return this.entries.size;
  }
}

/** Wrap a Blob/File in an object URL that this cache will revoke when evicted. */
export function cacheBlob(cache: ObjectUrlCache, key: string, blob: Blob): string {
  const cached = cache.get(key);
  if (cached) return cached;
  const url = URL.createObjectURL(blob);
  cache.set(key, url, () => URL.revokeObjectURL(url));
  return url;
}
